import { CommonModule } from '@angular/common';
import { Component, effect, NO_ERRORS_SCHEMA, OnDestroy, OnInit, signal, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowModule } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Permission, Role } from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { CasiCloseOptions, FormModes, AppEnities } from '../../common/constants.lib';
import { ToastrService } from 'ngx-toastr';
import { RolesService } from '../roles.service';
import {
  faPlusCircle, faAddressCard,
  faEdit, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { EditRoleModalComponent } from './edit-role-modal/edit-role-modal.component';
import { PermissionService } from '../permission.service';
import { RolePermissionComponent } from './role-permission/role-permission.component';

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';

const { ROLE } = AppEnities;

@Component({
  selector: 'casi-roles',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    RolePermissionComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss'
})
export class RolesComponent implements OnInit, OnDestroy {
  readonly rolesResource = this.dataService.createRolesResource();
  readonly permissionResource = this.permissionService.createPermissionResource();

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  public view: Observable<Role[]> = new Observable<Role[]>();
  public rawData: Role[] = [];
  _rawData = signal<Role[]>([]);

  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Role[] = filterBy(this.rawData, this.filter);
  _gridData = signal<Role[]>(filterBy(this._rawData(), this.filter));

  public permissionSet: Permission[] = []
  public _permissionSet = signal<Permission[]>([]);

  public currentRole: Role = new Role();

  public formMode: FormModes = FormModes.NOTSET;

  public editWinOpen: boolean = false;
  public errorWinOpen: boolean = false;
  public confirmDeleteRole: boolean = false;
  public accessRightsWinOpen: boolean = false;
  public errorMsgOpen: boolean = false;
  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: RolesService,
    public faIcons: FaIconLibrary,
    private permissionService: PermissionService,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private me: MeService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash
    );
    faIcons.addIcons(...all);

    effect(() => {
      this.logger.debug(`constructor:effect fired....`);
      this._rawData.set(this.rolesResource.value() ?? []);
      this._permissionSet.set(this.permissionResource.value() ?? []);
      this.logger.debug(`_rawData: `, this._rawData());
      this.logger.debug(`_permissionSet: `, this._permissionSet());
      this.loadData();
    })
  }

  public sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_destroy } = this.me.allICanDo(ROLE)
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canDestroy = can_destroy;
    // this.dataService.query();
    // this.permissionService.query();
    // this._subs.push(this.dataService.subscribe(v => {
    //   this.logger.debug('ngOnInit:v: ', v);
    //   this.rawData = v;
    //   this.loadData();
    // }));
    // this._subs.push(this.permissionService.subscribe(p => {
    //   this.logger.debug('ngOnInit:p: ', p);
    //   this.permissionSet = p;
    // }))
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  public onAddRole(): void {
    this.logger.debug("onAddRole...");
    this.currentRole = new Role();
    this.formMode = FormModes.NEW;
    this.logger.debug("onAddRole:currentRole ", this.currentRole);
    this.openAddEditDialog(this.formMode, this.currentRole);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentRole = Role.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentRole ", this.currentRole);
    this.openAddEditDialog(this.formMode, this.currentRole);
  }

  onEditPermisions(dataItem: any): void {
    this.logger.debug("onEditPermisions: ", dataItem, this._permissionSet());
    this.currentRole = dataItem;
    this.accessRightsWinOpen = true;
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentRole = dataItem;
    this.openConfirmDailog(this.currentRole);
  }

  onConfirmedClose(status: string): void {
    this.logger.debug("onConfirmedClose: ", status, this.currentRole);
    this.confirmDeleteRole = false;
    if (status === 'delete') {
      this.handleRemove(this.currentRole);
    }
  }

  //#region Grid function

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  public close(args: any): void {
    this.logger.debug('close...', args);
    this.editWinOpen = false;
    this.handleCloseDeprecated(args);
  }

  public closeAccessRights(): void {
    this.logger.debug('closeAccessRights...');
    this.accessRightsWinOpen = false;
  }

  public onDialogClose(): void {
    this.logger.debug('onDialogClose...');
    this.errorWinOpen = false;
  }

  public onErrorDialogClose(): void {
    this.logger.debug('onErrorDialogClose...');
    this.errorMsgOpen = false;
  }

  public onRolesPermissionSave(): void {
    this.logger.debug('onRolesPermissionSave...');
    this.handleAccessRightClose();
  }

  //#region Private function

  private openAddEditDialog(mode: FormModes, dataItem: Role): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: EditRoleModalComponent,
      minWidth: 320,
      width: 600,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as EditRoleModalComponent;
    contentRef.title = setTitle(mode, 'ROLE');
    contentRef.role = dataItem;
    contentRef.mode = mode;
    contentRef.existingRoles = this.rawData;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.form.value);
        this.handleClose(contentRef.form.value, mode);
      }
    });
  }

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this._gridData.set(orderBy(filterBy(this._rawData(), this.filter), this.sort));
    this.pageSize = this._gridData().length;

    // this.pageSize = this.gridData.length;
  }

  private handleCloseDeprecated(args: any): void {
    this.logger.debug('handleCloseDeprecated...', args.data, args.mode);
    let dup: boolean = false;
    if (args && args.data) {
      let inUse = this.rawData.find(r => r.name === args.data.name);
      this.logger.debug('handleCloseDeprecated...inUse', inUse);
      if (inUse) {
        if (args.mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== args.data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleCloseDeprecated: role: ${args.data.name} already in use `);
        this.openErrorDailog("Duplicate Role Name", "That Role already exists!", "Close and Retry")
        // this.errorWinOpen = true;

      } else
        this.saveRole(args.data, args.mode);
    }
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.saveRole(data, mode);
  }

  private handleAccessRightClose(): void {
    this.logger.debug('handleAccessRightClose...');
    this.rolesResource.reload();
    // this.dataService.query();
    this.accessRightsWinOpen = false;
  }

  private handleCheck(args: any): void {
    if (args && args.id) {
      // this.logger.error('handlRemove: No id given');
      this.dataService.destroyCheck(args.id)
        .then(resp => {
          this.logger.debug('handleRemove:resp: ', resp);
          if (resp.users)
            this.openErrorDailog("Update Issue", `The role ${this.currentRole.name} has users linked with it. The users are: ${resp.users} They won't be able to login if the role is deleted. Are you sure you want to proceed?`, 'Close', 'Delete');
          else
            this.handleRemove(this.currentRole);
        })
        .catch(err => {
          this.logger.error('handleCheck:err: ', err);
          this.notify("Error Deleting Role", true);
        });
    }


  }

  private handleRemove(args: any): void {
    if (args && args.id) {
      // this.logger.error('handlRemove: No id given');
      this.dataService.destroy(args.id)
        .then(resp => {
          this.logger.debug('handleRemove:resp: ', resp);
          this.notify("Role Deleted");
          // this.dataService.query();
          this.rolesResource.reload();
        })
        .catch(err => {
          this.logger.error('handleRemove:err: ', err);
          this.notify("Error Deleting Role", true);
          this.openErrorDailog("Update Issue", `The role ${this.currentRole.name} has permissions. Please remove permissions first before deleting.`);
        });
    }


  }

  private saveRole(role: any, mode: FormModes): void {
    this.logger.debug('saveRole...', role, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(role)
        .then(resp => {
          this.logger.debug('saveRole:update:resp: ', resp);
          this.notify("Role Updated");
          this.rolesResource.reload();
          // this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveRole:update:err: ', err);
          this.notify("Error Updating Role", true);
        });
    } else {
      this.dataService.add(role)
        .then(resp => {
          this.logger.debug('saveRole:add:resp: ', resp);
          this.notify("Role Added");
          this.rolesResource.reload();
          // this.dataService.query();
        })
        .catch(err => {
          this.logger.error('saveRole:add:err: ', err);
          this.notify("Error Creating Role", true);
        });
    }
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    this.currentRole = dataItem;
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Role - <b>${this.currentRole.name}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentRole);
        this.handleCheck(this.currentRole);
      }
    });
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close', buttonText2: string = ''): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
    contentRef.buttonText2 = buttonText2;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentRole);
        this.handleRemove(this.currentRole);
      }
    });

  }

  //#endregion

}
